import { useState, useEffect, useRef, useCallback } from 'react';

export const LAZY_SECTION_IN_VIEW_OPTIONS = { rootMargin: '0px 0px 600px 0px' };

export const useInView = (
  options: IntersectionObserverInit,
  callback?: () => void,
  once = true
) => {
  const [isInView, setIsInView] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const setRef = useCallback(
    (node: Element | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (node) {
        const observer = new IntersectionObserver(([entry]) => {
          if (entry?.isIntersecting) {
            setIsInView(true);
            callback?.();
            if (once) {
              observer.disconnect();
              observerRef.current = null;
            }
          }
        }, options);

        observer.observe(node);
        observerRef.current = observer;
      }
    },
    [options, once, callback]
  );

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const disconnect = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  }, []);

  return [setRef, isInView, { disconnect }] as const;
};
